import React, { createContext, useContext, useState, ReactNode } from 'react';

// Interface for the Region Context
export interface IRegionContext {
  selectedRegions: string[];
  setSelectedRegions: (regionIDs: string[]) => void;
}

// Create the context with undefined default (will throw error if used outside provider)
const RegionContext = createContext<IRegionContext | undefined>(undefined);

// Props for the RegionProvider component
interface RegionProviderProps {
  children: ReactNode;
  initialRegions?: string[];
}

// RegionProvider component that manages the region state  
export function RegionProvider({ children, initialRegions = [] }: RegionProviderProps) {
  const [selectedRegions, setSelectedRegions] = useState<string[]>(initialRegions);

  const contextValue: IRegionContext = {
    selectedRegions,
    setSelectedRegions: (regionIDs: string[]) => {
      // Validate the regionIDs array
      if (!Array.isArray(regionIDs)) {
        console.warn('Invalid region IDs provided to setSelectedRegions:', regionIDs);
        return;
      }
      
      // Validate each regionID is a string
      const validRegionIDs = regionIDs.filter(id => typeof id === 'string');
      if (validRegionIDs.length !== regionIDs.length) {
        console.warn('Some invalid region IDs filtered out:', regionIDs);
      }
      
      setSelectedRegions(validRegionIDs);
    },
  };

  return (
    <RegionContext.Provider value={contextValue}>
      {children}
    </RegionContext.Provider>
  );
}

// Custom hook to use the Region context
export function useRegion(): IRegionContext {
  const context = useContext(RegionContext);
  
  if (context === undefined) {
    throw new Error('useRegion must be used within a RegionProvider');
  }
  
  return context;
}